import { useQuery } from '@tanstack/react-query'
import { MetricPeriod } from '@/lib/utils'
import { analyticsService, StudioAnalyticsData } from '@/services/analyticsService'

// Реэкспортируем типы из сервиса для обратной совместимости
export type { PlanData, FunnelData } from '@/services/analyticsService'

export interface UseStudioAnalyticsParams {
  studioId: number
  currentPeriod: MetricPeriod | null
}

export interface UseStudioAnalyticsReturn extends Omit<StudioAnalyticsData, 'analytics'> {
  analytics: StudioAnalyticsData['analytics'] | null
  loading: boolean
  error: string | null
  refresh: () => void
}

const EMPTY_DAILY_DATA: StudioAnalyticsData['dailyData'] = []
const EMPTY_CONVERSION_DAILY_DATA: StudioAnalyticsData['conversionDailyData'] = []
const EMPTY_NEXT_PERIOD_HEAD_DATA: StudioAnalyticsData['nextPeriodHeadData'] = []
const EMPTY_FUNNEL_DATA: StudioAnalyticsData['funnelData'] = {
  leads: 0,
  appointments: 0,
  visits: 0,
  sales: 0
}

/**
 * Hook для загрузки и управления данными аналитики студии
 * Использует React Query для автоматического кэширования и управления состоянием
 * 
 * @example
 * const { analytics, dailyData, loading, error } = useStudioAnalytics({
 *   studioId: 1,
 *   currentPeriod: period
 * })
 */
export function useStudioAnalytics({
  studioId,
  currentPeriod
}: UseStudioAnalyticsParams): UseStudioAnalyticsReturn {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<StudioAnalyticsData, Error>({
    queryKey: ['studio-analytics', studioId, currentPeriod?.period_id],
    queryFn: async () => {
      if (!currentPeriod) {
        throw new Error('No current period provided')
      }
      return analyticsService.loadPeriodAnalytics(studioId, currentPeriod)
    },
    enabled: !!studioId && !!currentPeriod,
    staleTime: 5 * 60 * 1000, // Кэш на 5 минут
    gcTime: 10 * 60 * 1000, // Хранить в памяти 10 минут (ранее cacheTime)
  })

  // Возвращаем данные в формате, совместимом со старым API
  return {
    analytics: data?.analytics || null,
    dailyData: data?.dailyData || EMPTY_DAILY_DATA,
    conversionDailyData: data?.conversionDailyData || EMPTY_CONVERSION_DAILY_DATA,
    nextPeriodHeadData: data?.nextPeriodHeadData || EMPTY_NEXT_PERIOD_HEAD_DATA,
    planData: data?.planData || null,
    previousPeriodData: data?.previousPeriodData || null,
    funnelData: data?.funnelData || EMPTY_FUNNEL_DATA,
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refresh: () => { refetch() }
  }
}
